import React, { useState, useRef, useEffect, useContext } from "react";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { socketConnection } from "../../services/socket";

import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItemCustom";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { canAccessTicket } from "../../utils/ticketUtils";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
}));

const NotificationsPopOver = (volume) => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const [showPendingTickets, setShowPendingTickets] = useState(false);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true" });

	const [play] = useSound(alertSound, volume);
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	// Refs para ler sempre o valor mais recente dentro do socket sem precisar
	// reconectar a cada render (evita perder eventos por reconexão constante).
	const userRef = useRef(user);
	userRef.current = user;
	const showPendingRef = useRef(showPendingTickets);
	showPendingRef.current = showPendingTickets;

	// Fallback visual: pisca o título da aba quando o som puder estar bloqueado
	// pelo navegador (aba em segundo plano / sem interação prévia do usuário).
	const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "");
	const titleFlashRef = useRef(null);

	const stopTitleFlash = () => {
		if (titleFlashRef.current) {
			clearInterval(titleFlashRef.current);
			titleFlashRef.current = null;
			document.title = originalTitleRef.current;
		}
	};

	const startTitleFlash = () => {
		if (titleFlashRef.current || document.visibilityState === "visible") return;
		let toggled = false;
		titleFlashRef.current = setInterval(() => {
			document.title = toggled ? originalTitleRef.current : "💬 Nova mensagem!";
			toggled = !toggled;
		}, 1000);
	};

	useEffect(() => {
		const fetchSettings = async () => {
			try {

				if (user.allTicket === "enable") {
					setShowPendingTickets(true);
				}
			} catch (err) {
			  	toastError(err);
			}
		}
	  
		fetchSettings();
	}, []);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else {
			Notification.requestPermission();
		}
	}, [play]);

	useEffect(() => {
  const processNotifications = () => {
    let filteredTickets = tickets;

    if (!showPendingTickets) {
      filteredTickets = filteredTickets.filter(ticket => ticket.status !== "pending");
    }

    // ✅ Filtra por acesso à fila
    filteredTickets = filteredTickets.filter(ticket =>
      canAccessTicket(ticket, user, user.queues.map(q => q.id))
    );

    setNotifications(filteredTickets);
  };

  processNotifications();
}, [tickets, showPendingTickets, user]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const handleVisible = () => {
			if (document.visibilityState === "visible") stopTitleFlash();
		};
		document.addEventListener("visibilitychange", handleVisible);
		window.addEventListener("focus", stopTitleFlash);
		return () => {
			document.removeEventListener("visibilitychange", handleVisible);
			window.removeEventListener("focus", stopTitleFlash);
			stopTitleFlash();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const companyId = userRef.current.companyId;
		const socket = socketConnection({ companyId, userId: userRef.current.id });
		socket.on("connect", () => socket.emit("joinNotification"));

		socket.on(`company-${companyId}-ticket`, data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		});

		socket.on(`company-${companyId}-appMessage`, data => {
			const loggedUser = userRef.current;
			const isPending = data.ticket.status === "pending";
			if (
				data.action === "create" && !data.message.fromMe &&
				// Pendentes (1º contato) só notificam quando o usuário pode ver "todos os tickets"
				(!isPending || showPendingRef.current) &&
				(!data.message.read || isPending) &&
				(data.ticket.userId === loggedUser?.id || !data.ticket.userId) &&
				(loggedUser?.queues?.some(queue => (queue.id === data.ticket.queueId)) || !data.ticket.queueId)
			) {
				if (canAccessTicket(data.ticket, loggedUser, loggedUser.queues.map(q => q.id))) {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
						if (ticketIndex !== -1) {
							prevState[ticketIndex] = data.ticket;
							return [...prevState];
						}
						return [data.ticket, ...prevState];
					});

					const shouldNotNotificate =
						(data.message.ticketId === ticketIdRef.current &&
							document.visibilityState === "visible") ||
						(data.ticket.userId && data.ticket.userId !== loggedUser?.id) ||
						data.ticket.isGroup;

					if (shouldNotNotificate) return;

					handleNotifications(data);
				}
			}
		});

		return () => {
			socket.disconnect();
		};
		// Conexão estável: depende só de id/empresa (primitivos). Os demais
		// valores são lidos via ref para não recriar o socket a cada render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.companyId, user.id]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.uuid}`);
			// handleChangeTab(null, ticket.isGroup? "group" : "open");
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current();
		// Se a aba não estiver visível, pisca o título como reforço visual
		// (caso o navegador bloqueie o som por política de autoplay).
		startTitleFlash();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				color="inherit"
				style={{color:"white"}}
			>
				<Badge overlap="rectangular" badgeContent={notifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;